import { ReactElement, useState } from "react";

import { AllIntegrationTypes } from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import Loading from "~frontend/components/Loading";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import { TableBox, TableControls } from "~frontend/components/Table";
import { useIntegrations } from "~frontend/hooks/useIntegrations";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";

import { AddIntegrationDialog } from "./AddIntegrationDialog";
import { EditIntegrationDialog } from "./EditIntegrationDialog";
import { IntegrationTable } from "./IntegrationTable";
import { useDeleteIntegration } from "./useDeleteIntegration";

export type AfterClose = {
  action: "edit";
  integration: AllIntegrationTypes;
};

export function IntegrationList(): ReactElement {
  const { subaccount, subaccountLink } = useSubaccount()!;
  useBreadcrumbs([
    {
      label: "Integrations",
      path: subaccountLink("/integrations"),
    },
  ]);

  const { loading, integrations } = useIntegrations();

  const [integrationToEdit, setIntegrationToEdit] = useState(
    null as AllIntegrationTypes | null
  );

  const [addIntegrationDialogOpen, setAddIntegrationDialogOpen] =
    useState(false);

  const { handleIntegrationDelete } = useDeleteIntegration();

  return (
    <PageContainer>
      <FlexBox alignItems="center" justifyContent="space-between">
        <PageHeader>Integrations</PageHeader>
        <TableControls
          addButtons={[
            {
              label: "Add new integration",
              onClick: () => {
                setAddIntegrationDialogOpen(true);
              },
              id: "add-new-integration",
            },
          ]}
        />
      </FlexBox>
      <TableBox>
        {loading ? (
          <Loading />
        ) : (
          <IntegrationTable
            rows={integrations}
            handleEditIntegration={setIntegrationToEdit}
            handleDeleteIntegration={handleIntegrationDelete}
          />
        )}
      </TableBox>
      <AddIntegrationDialog
        open={addIntegrationDialogOpen}
        onClose={(afterClose?: AfterClose) => {
          setAddIntegrationDialogOpen(false);
          // if the add integration dialog calls its onClose with a new integration
          // then we immediately switch to the edit dialog
          if (afterClose && afterClose.action === "edit") {
            setIntegrationToEdit(afterClose.integration);
          }
        }}
        subaccountId={subaccount.id}
      />
      <EditIntegrationDialog
        integration={integrationToEdit}
        onClose={() => setIntegrationToEdit(null)}
        subaccountId={subaccount.id}
      />
    </PageContainer>
  );
}
